// Per-type order data schemas and discriminated union
// Each order type has its own data schema with an `orderType` discriminator
import { z } from 'zod/v4';
import { CaseTypeRefSchema, CaseSubTypeRefSchema, CourtJurisdictionRefSchema, JurisdictionalAmountSchema, AdditionalCaseInfoSchema, PremisesAddressSchema, CaseTypeMetadataSchema, EFilingPartySchema, EFilingAttorneySchema, } from '../cases/case.schema.js';
import { EFilingDocumentSchema } from '../efiling/filing.schema.js';
// ---------------------------------------------------------------------------
// Shared sub-schemas
// ---------------------------------------------------------------------------
export const AddressTypeSchema = z.enum(['business', 'residence', 'other']);
export const ServeAddressSchema = z.object({
    serviceLevel: z.string(),
    addressType: AddressTypeSchema,
    businessName: z.string().optional(),
    street1: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
});
export const ForeclosureAddressSchema = z.object({
    addressType: AddressTypeSchema,
    businessName: z.string().optional(),
    street1: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
});
export const CapacitySchema = z.enum([
    'association-or-partnership',
    'authorized-person',
    'business-organization-form-unknown',
    'corporation',
    'defunct-corporation',
    'estate',
    'fictitious',
    'individual',
    'joint-stock-company-association',
    'minor',
    'occupant-prejudgment-claim',
    'public-entity',
    'sole-proprietorship',
    'trust',
]);
export const ServePartySchema = z.object({
    name: z.string(),
    capacity: CapacitySchema,
    registeredAgent: z.string().optional(),
});
export const WritTypeSchema = z.enum([
    'sheriff-and-serve',
    'sheriff-only',
    'serve-only',
    'sheriff-county-record-and-serve',
]);
export const RealWritTypeSchema = z.enum([
    'execution',
    'attachment',
    'earnings-withholding-order',
]);
export const ProofOptionSchema = z.enum(['file', 'notarize', 'none']);
export const ProofOfServiceSchema = z.object({
    attorneyOfRecordId: z.string().optional(),
    attorneyRepresents: z.string().optional(),
    repAttorneyName: z.string().optional(),
    repAttorneyBarId: z.string().optional(),
    overrideProofInfo: z.boolean().default(false),
    overrideName: z.string().optional(),
    overrideAddress: z.string().optional(),
    overrideAddressCSZ: z.string().optional(),
    overridePhone: z.string().optional(),
});
export const NoticePartySchema = z.object({
    name: z.string(),
    role: z.string(),
    addressType: AddressTypeSchema,
    businessName: z.string().optional(),
    address1: z.string(),
    address2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    noticeDate: z.string().optional(),
    noticeFromCity: z.string().optional(),
    noticeBy: z.string().optional(),
});
export const SkipTraceAddressSchema = z.object({
    addressType: AddressTypeSchema,
    businessName: z.string().optional(),
    street1: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
});
// ---------------------------------------------------------------------------
// Location/retrieval enums
// ---------------------------------------------------------------------------
export const RetrievalLocationTypeSchema = z.enum([
    'state-court',
    'county-recorders',
    'secretary-of-state',
    'federal-court',
    'archives',
]);
export const RetrievalTypeSchema = z.enum(['same-case', 'different-case']);
export const CopyTypeSchema = z.enum(['certified', 'plain']);
// ---------------------------------------------------------------------------
// Per-type data schemas (9 total)
// ---------------------------------------------------------------------------
// 1. Process Serving
export const ProcessServingDataSchema = z.object({
    orderType: z.literal('process-serving'),
    writType: WritTypeSchema.optional(),
    realWritType: RealWritTypeSchema.optional(),
    parties: z.array(ServePartySchema).min(1).max(15),
    serveAddresses: z.array(ServeAddressSchema).default([]),
    hearingDateTime: z.string().optional(),
    timezone: z.string().optional(),
    departmentDivision: z.string().optional(),
    advanceWitnessFees: z.boolean().default(false),
    proofOption: ProofOptionSchema.default('none'),
    serveSameDocuments: z.boolean().default(false),
    serveSameAddress: z.boolean().default(false),
    alternateServiceNoAddress: z.boolean().default(false),
    specialInstructions: z.string().optional(),
    foreclosureAddresses: z.array(ForeclosureAddressSchema).default([]),
    proofOfService: ProofOfServiceSchema.optional(),
    noticeParties: z.array(NoticePartySchema).default([]),
});
// 2. Court Filing (physical/manual — optionally chains into process serving)
export const CourtFilingDataSchema = z.object({
    orderType: z.literal('court-filing'),
    filingState: z.string(),
    filingCounty: z.string(),
    filingType: z.enum(['case-initiation', 'subsequent-filing']),
    caseCategory: z.enum(['family', 'non-family']).optional(),
    jurisdictionId: z.string().optional(),
    isFederalOrFamily: z.boolean().default(false),
    isNonEFile: z.boolean().default(false),
    eServeCaseParticipants: z.boolean().default(false),
    autoCreateProof: z.boolean().default(false),
    isFirstAppearance: z.boolean().default(false),
    isUnlawfulDetainer: z.boolean().default(false),
    isChangingAttorneys: z.boolean().default(false),
    isAttorneyChangingAddress: z.boolean().default(false),
    serveAfterFiling: z.boolean().default(false),
    serveData: z.object({
        parties: z.array(ServePartySchema).default([]),
        serveAddresses: z.array(ServeAddressSchema).default([]),
        hearingDateTime: z.string().optional(),
        timezone: z.string().optional(),
        departmentDivision: z.string().optional(),
        advanceWitnessFees: z.boolean().default(false),
        proofOption: ProofOptionSchema.default('none'),
        serveSameDocuments: z.boolean().default(false),
        serveSameAddress: z.boolean().default(false),
        alternateServiceNoAddress: z.boolean().default(false),
        specialInstructions: z.string().optional(),
    }).optional(),
});
// 3. Document Retrieval
export const DocumentRetrievalDataSchema = z.object({
    orderType: z.literal('document-retrieval'),
    retrievalType: RetrievalTypeSchema,
    locationType: RetrievalLocationTypeSchema.optional(),
    locationState: z.string().optional(),
    locationCounty: z.string().optional(),
    jurisdictionId: z.string().optional(),
    caseNumberToCopy: z.string().optional(),
    isEntireFile: z.boolean().default(false),
    isCertainDocuments: z.boolean().default(false),
    certainDocumentNames: z.string().optional(),
    isRegisterOfAction: z.boolean().default(false),
    copyType: CopyTypeSchema.default('plain'),
    pageLimit: z.number().int().min(0).max(9999).default(0),
    authorizeCourtFees: z.boolean().default(true),
    searchTarget: z.string().optional(),
    searchLocation: z.string().optional(),
});
// 4. Courtesy Copy Delivery
export const CourtesyCopyDeliveryDataSchema = z.object({
    orderType: z.literal('courtesy-copy-delivery'),
    state: z.string().default('CA'),
    county: z.string(),
    isFamily: z.boolean().default(false),
});
// 5. Secretary of State Filing
export const SecretaryOfStateFilingDataSchema = z.object({
    orderType: z.literal('secretary-of-state-filing'),
    state: z.string().default('CA'),
    jurisdiction: z.string().default('Sacramento'),
});
// 6. County Recording
export const CountyRecordingDataSchema = z.object({
    orderType: z.literal('county-recording'),
    state: z.string().default('CA'),
    county: z.string(),
    jurisdictionId: z.string(),
});
// 7. Skip Trace
export const SkipTraceDataSchema = z.object({
    orderType: z.literal('skip-trace'),
    subjectName: z.string(),
    subjectGender: z.enum(['male', 'female']).optional(),
    subjectDOB: z.string().optional(),
    lastKnownAddresses: z.array(SkipTraceAddressSchema).default([]),
    addressNotAvailable: z.boolean().default(false),
    specialInstructions: z.string().optional(),
    isLastKnownAddressRequired: z.boolean().default(true),
});
// 8. eService
export const EServiceDataSchema = z.object({
    orderType: z.literal('eservice'),
    state: z.string(),
    county: z.string(),
    jurisdictionId: z.string().optional(),
    eFileLocationCode: z.string().optional(),
    caseNumberEFMToPhysical: z.string().optional(),
    isFamily: z.boolean().default(false),
});
// 9. eFiling (wraps existing EFilingWizardState)
export const EFilingDataSchema = z.object({
    orderType: z.literal('e-filing'),
    orderInfo: z.object({
        state: z.string(),
        county: z.string(),
        // Optional for backward compatibility only (SOR-155): the Federal e-filing
        // option was removed. Old persisted drafts may still carry the field.
        isFederal: z.boolean().optional(),
        filingType: z.string(), // 'case-initiation' | 'subsequent-filing' | ''
        serveAfterFiling: z.boolean(),
        eServeCaseParticipants: z.boolean(),
    }),
    caseInfo: z.object({
        caseTitle: z.string(),
        caseCategoryCode: z.string(),
        caseCategoryName: z.string(),
        caseType: CaseTypeRefSchema.nullable(),
        caseSubType: CaseSubTypeRefSchema.nullable(),
        jurisdictionalAmount: z.union([JurisdictionalAmountSchema, z.literal('')]),
        courtJurisdiction: CourtJurisdictionRefSchema.nullable(),
        incidentZip: z.string(),
        trialCourtCaseNumber: z.string(),
        additionalInfo: AdditionalCaseInfoSchema,
        demandAmount: z.number(),
        premisesAddress: PremisesAddressSchema,
        caseTypeMetadata: CaseTypeMetadataSchema.nullable(),
    }),
    parties: z.array(EFilingPartySchema),
    attorneys: z.array(EFilingAttorneySchema),
    documents: z.array(EFilingDocumentSchema),
    courtFees: z.object({
        fees: z.object({
            courtFees: z.number().default(0),
            convenienceFee: z.number().default(0),
            courtTransactionFee: z.number().default(0),
            totalCourtFees: z.number().default(0),
            serviceFee: z.number().default(0),
            processingFee: z.number().default(0),
            totalFees: z.number().default(0),
        }),
        authorizePayment: z.boolean(),
    }),
    orderDetails: z.object({
        serviceLevel: z.string(),
        messageToClerk: z.string(),
        paymentMethodId: z.string(),
        internalReferenceNumber: z.string(),
    }),
});
// ---------------------------------------------------------------------------
// Discriminated union
// ---------------------------------------------------------------------------
export const OrderTypeDataSchema = z.discriminatedUnion('orderType', [
    ProcessServingDataSchema,
    CourtFilingDataSchema,
    DocumentRetrievalDataSchema,
    CourtesyCopyDeliveryDataSchema,
    SecretaryOfStateFilingDataSchema,
    CountyRecordingDataSchema,
    SkipTraceDataSchema,
    EServiceDataSchema,
    EFilingDataSchema,
]);
